// P2PChat.jsx
import React, { useState, useEffect, useRef } from 'react';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import * as Avatar from '@radix-ui/react-avatar';
import * as Tooltip from '@radix-ui/react-tooltip';
import * as Separator from '@radix-ui/react-separator';
import * as Label from '@radix-ui/react-label';
import { useNoiseSuppression } from '../hooks/useNoiseSuppression';
import './P2PChat.css';

const ADJECTIVES = ['swift', 'brave', 'calm', 'bright', 'bold', 'kind', 'wise', 'free'];
const NOUNS = ['lion', 'eagle', 'river', 'star', 'stone', 'flame', 'oak', 'tide'];

function generateReadableId() {
  const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  const num = Math.floor(Math.random() * 999);
  return `${adj}-${noun}-${num}`;
}

function getAvatarColor(id) {
  const palette = ['#7c3aed', '#2563eb', '#059669', '#b45309', '#db2777', '#0891b2', '#dc2626', '#65a30d'];
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
}

const P2PChat = ({ user = null, onShowAuth, onSignOut, initialPeerId = null }) => {
  // ── Chat State ────────────────────────────────────────────────────────────
  const [localId, setLocalId] = useState('');
  const [peerId, setPeerId] = useState('');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [connections, setConnections] = useState({});
  const [participants, setParticipants] = useState([]);
  const [connected, setConnected] = useState(false);
  const [username, setUsername] = useState(`User-${Math.floor(Math.random() * 1000)}`);
  const [customId, setCustomId] = useState(initialPeerId ?? generateReadableId);
  const [peerReady, setPeerReady] = useState(false);
  const [idError, setIdError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // ── Voice State ───────────────────────────────────────────────────────────
  const [voiceActive, setVoiceActive] = useState(false);
  const [muted, setMuted] = useState(false);         // self-muted (user's own choice)
  const [hostMuted, setHostMuted] = useState(false); // muted by host — cannot self-unmute
  const [voiceParticipants, setVoiceParticipants] = useState(new Set());
  const [speakingPeers, setSpeakingPeers] = useState(new Set());
  const [voiceError, setVoiceError] = useState('');
  const [noiseSuppression, setNoiseSuppression] = useState(true);

  // ── Host State ────────────────────────────────────────────────────────────
  // hostId is set to localId when someone connects TO us (inbound),
  // or learned from user-info when we connect to someone else.
  const [hostId, setHostId] = useState('');
  // Tracks which peers the host has force-muted (so the button toggles correctly)
  const [hostMutedPeers, setHostMutedPeers] = useState(new Set());

  // ── Chat Refs ─────────────────────────────────────────────────────────────
  const peerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const connectionsRef = useRef(connections);
  const localIdRef = useRef('');
  const hostIdRef = useRef('');

  // ── Voice Refs ────────────────────────────────────────────────────────────
  const voiceActiveRef = useRef(false);
  const mutedRef = useRef(false);
  const localStreamRef = useRef(null);       // raw mic stream — used for mute (track.enabled)
  const processedStreamRef = useRef(null);   // NS-processed stream — sent over WebRTC
  const mediaConnectionsRef = useRef({});
  const audioElementsRef = useRef({});
  const speakingAnalysersRef = useRef({});
  const ns = useNoiseSuppression();

  // ── Sync Refs ─────────────────────────────────────────────────────────────
  useEffect(() => { connectionsRef.current = connections; }, [connections]);
  useEffect(() => { voiceActiveRef.current = voiceActive; }, [voiceActive]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { localIdRef.current = localId; }, [localId]);
  useEffect(() => { hostIdRef.current = hostId; }, [hostId]);

  // ── Auto-scroll ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (peerRef.current) peerRef.current.destroy();
      teardownVoice();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ───────────────────────────────────────────────────────────────
  const sanitizeId = (input) =>
    input.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const handleCustomIdChange = (e) => setCustomId(sanitizeId(e.target.value));

  const handleCopy = () => {
    navigator.clipboard.writeText(localId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Speaking Detection ────────────────────────────────────────────────────
  const startSpeakingDetection = (pid, stream) => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      audioCtx.createMediaStreamSource(stream).connect(analyser);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const intervalId = setInterval(() => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setSpeakingPeers(prev => {
          const next = new Set(prev);
          if (avg > 8) next.add(pid);
          else next.delete(pid);
          return next;
        });
      }, 80);

      speakingAnalysersRef.current[pid] = { audioCtx, intervalId };
    } catch (err) {
      console.warn('Speaking detection unavailable:', err);
    }
  };

  const stopSpeakingDetection = (pid) => {
    const info = speakingAnalysersRef.current[pid];
    if (info) {
      clearInterval(info.intervalId);
      info.audioCtx.close().catch(() => {});
      delete speakingAnalysersRef.current[pid];
    }
    setSpeakingPeers(prev => {
      const next = new Set(prev);
      next.delete(pid);
      return next;
    });
  };

  // ── Audio Element Management ──────────────────────────────────────────────
  const playRemoteAudio = (pid, stream) => {
    let audio = audioElementsRef.current[pid];
    if (!audio) {
      audio = document.createElement('audio');
      audio.autoplay = true;
      document.body.appendChild(audio);
      audioElementsRef.current[pid] = audio;
    }
    audio.srcObject = stream;
    startSpeakingDetection(pid, stream);
  };

  const removeRemoteAudio = (pid) => {
    const audio = audioElementsRef.current[pid];
    if (audio) {
      audio.srcObject = null;
      audio.remove();
      delete audioElementsRef.current[pid];
    }
    stopSpeakingDetection(pid);
  };

  // ── Media Connection Setup ────────────────────────────────────────────────
  const setupMediaConnection = (call) => {
    mediaConnectionsRef.current[call.peer] = call;

    call.on('stream', (remoteStream) => {
      playRemoteAudio(call.peer, remoteStream);
    });

    call.on('close', () => {
      removeRemoteAudio(call.peer);
      delete mediaConnectionsRef.current[call.peer];
      setVoiceParticipants(prev => {
        const next = new Set(prev);
        next.delete(call.peer);
        return next;
      });
    });

    call.on('error', (err) => {
      console.error('Media connection error:', err);
      removeRemoteAudio(call.peer);
      delete mediaConnectionsRef.current[call.peer];
    });
  };

  const callPeer = (pid, stream) => {
    if (!peerRef.current || mediaConnectionsRef.current[pid]) return;
    const call = peerRef.current.call(pid, stream);
    setupMediaConnection(call);
  };

  // ── Voice Controls ────────────────────────────────────────────────────────
  const joinVoice = async () => {
    setVoiceError('');

    if (!navigator.mediaDevices?.getUserMedia) {
      setVoiceError('Voice chat requires a secure connection (HTTPS or localhost).');
      return;
    }

    try {
      // ── Tier 1: engage browser-native audio processing ─────────────────
      // noiseSuppression/echoCancellation/autoGainControl activate the browser's
      // built-in WebRTC APM (hardware-accelerated in Chrome/Edge) at zero added latency.
      // These are "ideal" hints — the browser grants what it supports.
      const rawStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl:  { ideal: true },
          sampleRate:       { ideal: 48000 },
          channelCount:     { ideal: 1 },
          latency:          { ideal: 0.01 },
        },
      });

      // ── Tier 2: pipe through RNNoise AudioWorklet ───────────────────────
      // The worklet's VAD output drives the speaking indicator — replacing the
      // frequency-analyser loop for the local user with RNNoise's own detection.
      const processedStream = await ns.setup(rawStream, {
        enabled: noiseSuppression,
        onVad: (prob) => {
          const lid = localIdRef.current;
          if (!mutedRef.current) {
            setSpeakingPeers(prev => {
              const next = new Set(prev);
              if (prob > 0.5) next.add(lid); else next.delete(lid);
              return next;
            });
          }
        },
      });

      // rawStream      → track.enabled handles self-mute and host-mute
      // processedStream → what travels over the WebRTC connection
      localStreamRef.current    = rawStream;
      processedStreamRef.current = processedStream;

      setVoiceActive(true);
      voiceActiveRef.current = true;

      const lid = localIdRef.current;
      setVoiceParticipants(prev => new Set([...prev, lid]));
      // Local speaking detection is now driven by the worklet's onVad callback above.
      // Remote peers still use the frequency analyser (started in playRemoteAudio).
      broadcastToPeers({ type: 'voice-joined', peerId: lid });

      // Call all currently connected peers with the processed stream
      Object.keys(connectionsRef.current).forEach(pid => callPeer(pid, processedStream));
    } catch (err) {
      console.error('Mic access failed:', err);
      ns.teardown();
      setVoiceError(
        err.name === 'NotAllowedError'
          ? 'Mic access denied. Please allow mic access and try again.'
          : 'Could not access microphone.'
      );
    }
  };

  const leaveVoice = () => {
    const lid = localIdRef.current;

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    ns.teardown();
    processedStreamRef.current = null;

    stopSpeakingDetection(lid);

    Object.values(mediaConnectionsRef.current).forEach(call => {
      try { call.close(); } catch (_) {}
    });
    mediaConnectionsRef.current = {};

    Object.keys(audioElementsRef.current).forEach(pid => removeRemoteAudio(pid));

    broadcastToPeers({ type: 'voice-left', peerId: lid });

    setVoiceActive(false);
    voiceActiveRef.current = false;
    setMuted(false);
    setHostMuted(false);
    setHostMutedPeers(new Set());
    setVoiceParticipants(prev => {
      const next = new Set(prev);
      next.delete(lid);
      return next;
    });
  };

  // Full teardown for unmount — no state updates needed
  const teardownVoice = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
    }
    ns.teardown();
    processedStreamRef.current = null;
    Object.values(mediaConnectionsRef.current).forEach(call => {
      try { call.close(); } catch (_) {}
    });
    Object.keys({ ...audioElementsRef.current }).forEach(pid => {
      const audio = audioElementsRef.current[pid];
      if (audio) { audio.srcObject = null; audio.remove(); }
    });
    Object.values(speakingAnalysersRef.current).forEach(({ audioCtx, intervalId }) => {
      clearInterval(intervalId);
      audioCtx.close().catch(() => {});
    });
  };

  const toggleMute = () => {
    // If the host has muted this user, they cannot unmute themselves
    if (hostMuted || !localStreamRef.current) return;
    const nowMuted = !muted;
    localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !nowMuted; });
    setMuted(nowMuted);
    if (nowMuted) {
      setSpeakingPeers(prev => {
        const next = new Set(prev);
        next.delete(localIdRef.current);
        return next;
      });
    }
  };

  const toggleNoiseSuppression = () => {
    const next = !noiseSuppression;
    setNoiseSuppression(next);
    ns.setEnabled(next);
  };

  // Host toggles mute/unmute for a specific peer
  const requestMutePeer = (pid) => {
    const conn = connectionsRef.current[pid]?.connection;
    if (!conn?.open) return;
    if (hostMutedPeers.has(pid)) {
      // Currently host-muted — release the lock
      conn.send({ type: 'voice-unmute-request' });
      setHostMutedPeers(prev => {
        const next = new Set(prev);
        next.delete(pid);
        return next;
      });
    } else {
      // Mute them
      conn.send({ type: 'voice-mute-request' });
      setHostMutedPeers(prev => new Set([...prev, pid]));
    }
  };

  // ── Peer Initialization ───────────────────────────────────────────────────
  const initializePeer = (id) => {
    const sanitized = sanitizeId(id);
    if (!sanitized) return;
    setIdError('');

    import('peerjs').then(({ default: Peer }) => {
      const peer = new Peer(sanitized);

      peer.on('open', (assignedId) => {
        setLocalId(assignedId);
        localIdRef.current = assignedId;
        setParticipants(prev => [...prev, { id: assignedId, name: `${username} (you)` }]);
        setPeerReady(true);
      });

      peer.on('connection', (conn) => handleNewConnection(conn, true));

      // Incoming voice call
      peer.on('call', (call) => {
        if (voiceActiveRef.current && processedStreamRef.current) {
          call.answer(processedStreamRef.current);
          setupMediaConnection(call);
        }
        // If not in voice, we don't answer — the caller will get no stream back.
        // The peer's MediaConnection will timeout/close naturally.
      });

      peer.on('error', (err) => {
        if (err.type === 'unavailable-id') {
          setIdError('That ID is already taken. Please choose another.');
          peer.destroy();
          setPeerReady(false);
        } else {
          console.error('Peer error:', err);
        }
      });

      peerRef.current = peer;
    });
  };

  // ── Connection Handling ───────────────────────────────────────────────────
  const handleNewConnection = (conn, isInbound = false) => {
    conn.on('open', () => {
      // If someone connected TO us and no host exists yet, we become host
      if (isInbound && !hostIdRef.current) {
        hostIdRef.current = localIdRef.current;
        setHostId(localIdRef.current);
      }

      conn.send({ type: 'user-info', username, peerId: localIdRef.current, hostId: hostIdRef.current });

      setConnections(prev => ({
        ...prev,
        [conn.peer]: { connection: conn, username: `Peer ${conn.peer.substring(0, 5)}` }
      }));

      setParticipants(prev => {
        if (!prev.some(p => p.id === conn.peer)) {
          return [...prev, { id: conn.peer, name: `Peer ${conn.peer.substring(0, 5)}` }];
        }
        return prev;
      });

      conn.send({ type: 'known-peers', peers: Object.keys(connectionsRef.current) });

      // Let the new peer know we're in voice so they can call us
      if (voiceActiveRef.current) {
        conn.send({ type: 'voice-joined', peerId: localIdRef.current });
      }

      setConnected(true);

      setMessages(prev => [...prev, {
        id: `system-${Date.now()}`,
        sender: 'system',
        content: `Connected to ${conn.peer.substring(0, 12)}…`,
        timestamp: new Date()
      }]);
    });

    conn.on('data', (data) => handleIncomingData(data, conn.peer));

    conn.on('close', () => {
      const peerUsername =
        connectionsRef.current[conn.peer]?.username || `Peer ${conn.peer.substring(0, 5)}`;

      setConnections(prev => {
        const next = { ...prev };
        delete next[conn.peer];
        return next;
      });

      setParticipants(prev => prev.filter(p => p.id !== conn.peer));

      // Clean up voice for this peer
      if (mediaConnectionsRef.current[conn.peer]) {
        try { mediaConnectionsRef.current[conn.peer].close(); } catch (_) {}
        delete mediaConnectionsRef.current[conn.peer];
      }
      removeRemoteAudio(conn.peer);
      setVoiceParticipants(prev => {
        const next = new Set(prev);
        next.delete(conn.peer);
        return next;
      });

      setMessages(prev => [...prev, {
        id: `system-${Date.now()}`,
        sender: 'system',
        content: `${peerUsername} left the chat.`,
        timestamp: new Date()
      }]);

      setConnected(Object.keys(connectionsRef.current).length > 1);
    });
  };

  // ── Incoming Data ─────────────────────────────────────────────────────────
  const handleIncomingData = (data, senderId) => {
    switch (data.type) {
      case 'chat-message': {
        const senderName =
          connectionsRef.current[senderId]?.username || `Peer ${senderId.substring(0, 5)}`;
        setMessages(prev => [...prev, {
          id: `${senderId}-${Date.now()}`,
          sender: senderId,
          senderName,
          content: data.content,
          timestamp: new Date()
        }]);
        break;
      }
      case 'known-peers':
        data.peers.forEach(pid => {
          if (pid !== localIdRef.current && !connectionsRef.current[pid]) {
            connectToPeer(pid);
          }
        });
        break;
      case 'system-message':
        setMessages(prev => [...prev, {
          id: `system-${Date.now()}`,
          sender: 'system',
          content: data.content,
          timestamp: new Date()
        }]);
        break;
      case 'user-info':
        setConnections(prev => ({
          ...prev,
          [senderId]: { ...prev[senderId], username: data.username }
        }));
        setParticipants(prev => [
          ...prev.filter(p => p.id !== senderId),
          { id: senderId, name: data.username }
        ]);
        // Learn who the host is if we don't know yet
        if (!hostIdRef.current && data.hostId) {
          hostIdRef.current = data.hostId;
          setHostId(data.hostId);
        }
        break;
      case 'voice-joined': {
        const pid = data.peerId || senderId;
        setVoiceParticipants(prev => new Set([...prev, pid]));
        // If we're in voice, initiate a call to this peer with the processed stream
        if (voiceActiveRef.current && processedStreamRef.current) {
          callPeer(pid, processedStreamRef.current);
        }
        break;
      }
      case 'voice-left': {
        const pid = data.peerId || senderId;
        setVoiceParticipants(prev => {
          const next = new Set(prev);
          next.delete(pid);
          return next;
        });
        if (mediaConnectionsRef.current[pid]) {
          try { mediaConnectionsRef.current[pid].close(); } catch (_) {}
          delete mediaConnectionsRef.current[pid];
        }
        removeRemoteAudio(pid);
        setHostMutedPeers(prev => {
          const next = new Set(prev);
          next.delete(pid);
          return next;
        });
        break;
      }
      case 'voice-mute-request':
        // Only honor mute requests from the actual host
        if (senderId !== hostIdRef.current) break;
        if (localStreamRef.current) {
          localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = false; });
          setHostMuted(true);
          setSpeakingPeers(prev => {
            const next = new Set(prev);
            next.delete(localIdRef.current);
            return next;
          });
          setMessages(prev => [...prev, {
            id: `system-${Date.now()}`,
            sender: 'system',
            content: 'You were muted by the host.',
            timestamp: new Date()
          }]);
        }
        break;
      case 'voice-unmute-request':
        // Only honor unmute requests from the actual host
        if (senderId !== hostIdRef.current) break;
        setHostMuted(false);
        // Re-enable mic only if the user hasn't also self-muted
        if (localStreamRef.current && !mutedRef.current) {
          localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = true; });
        }
        setMessages(prev => [...prev, {
          id: `system-${Date.now()}`,
          sender: 'system',
          content: 'The host unmuted you.',
          timestamp: new Date()
        }]);
        break;
      default:
        console.log('Unknown message type:', data.type);
    }
  };

  // ── Peer Connection ───────────────────────────────────────────────────────
  const connectToPeer = (id) => {
    if (!peerRef.current || id === localIdRef.current || connectionsRef.current[id]) return;
    const conn = peerRef.current.connect(id);
    handleNewConnection(conn);
  };

  const handleConnectClick = () => {
    if (peerId.trim()) {
      connectToPeer(peerId.trim());
      setPeerId('');
    }
  };

  // ── Messages ──────────────────────────────────────────────────────────────
  const handleSendMessage = () => {
    if (!newMessage.trim() || Object.keys(connections).length === 0) return;

    const messageObj = {
      id: `${localId}-${Date.now()}`,
      sender: localId,
      senderName: username,
      content: newMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, messageObj]);
    broadcastToPeers({ type: 'chat-message', content: newMessage });
    setNewMessage('');
  };

  const broadcastToPeers = (data) => {
    Object.values(connections).forEach(({ connection }) => {
      if (connection?.open) connection.send(data);
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleUsernameChange = (e) => {
    const newName = e.target.value;
    setUsername(newName);
    setParticipants(prev => [
      ...prev.filter(p => p.id !== localId),
      { id: localId, name: `${newName} (you)` }
    ]);
    broadcastToPeers({ type: 'user-info', username: newName, peerId: localId });
  };

  const disconnectFromPeer = (pid) => {
    const conn = connections[pid]?.connection;
    if (conn?.open) conn.close();
  };

  const getParticipantName = (id) => {
    const p = participants.find(p => p.id === id);
    return p ? p.name.replace(' (you)', '') : `Peer ${id.substring(0, 5)}`;
  };

  // ── Lobby Screen ──────────────────────────────────────────────────────────
  if (!peerReady) {
    return (
      <div className="chat-lobby">
        <div className="lobby-card">
          <div className="lobby-header">
            <span className="lobby-logo">✦</span>
            <h1 className="lobby-title">P2PChat</h1>
            <p className="lobby-subtitle">Secure peer-to-peer messaging</p>
          </div>

          <div className="lobby-form">
            <Label.Root className="form-label">Your Chat ID</Label.Root>

            <div className="id-input-group">
              <input
                type="text"
                value={customId}
                readOnly
                className="input"
              />
              <button
                onClick={() => setCustomId(generateReadableId())}
                className="btn-icon"
                title="Generate new ID"
              >
                ↻
              </button>
            </div>

            <input
              type="text"
              value={customId}
              onChange={handleCustomIdChange}
              placeholder="Or type a custom ID…"
              className="input"
            />

            {idError && <p className="error-text">{idError}</p>}

            <button
              onClick={() => initializePeer(customId)}
              disabled={!customId.trim()}
              className="btn btn-primary btn-lg btn-full"
              style={{ marginTop: 4 }}
            >
              Start Chatting
            </button>
            <p className="lobby-subtitle">🔔 <strong>Remember:</strong> Messages are never stored on a server, but that doesn't mean the people you're chatting with aren't recording your messages. Be kind! v0.6</p>
          </div>

          <div className="lobby-auth-row">
            {user ? (
              <>
                <span className="lobby-auth-status">
                  Signed in as <strong>{user.user_metadata?.username || user.email}</strong>
                </span>
                <button className="lobby-auth-link" onClick={onSignOut}>Sign out</button>
              </>
            ) : (
              <button className="lobby-auth-link" onClick={onShowAuth}>
                Sign in or create an account
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Main Chat Screen ──────────────────────────────────────────────────────
  return (
    <Tooltip.Provider delayDuration={300}>
      <div className="chat-app">

        {/* ── Left Sidebar ── */}
        <aside className={`chat-sidebar${sidebarOpen ? ' sidebar-open' : ''}`}>
          <div className="sidebar-header">
            <span className="app-logo">✦</span>
            <span className="app-name">P2PChat</span>
          </div>

          <div className="sidebar-body">
            <div className="sidebar-sections">

              {/* CONNECT TO PEER */}
              {!connected && (
                <div className="sidebar-section">
                  <h3 className="section-label">Connect to a Chat</h3>
                  <div className="connect-row">
                    <input
                      type="text"
                      value={peerId}
                      onChange={(e) => setPeerId(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleConnectClick()}
                      placeholder="Enter chat ID…"
                      className="input"
                      style={{ fontSize: 13 }}
                    />
                    <button onClick={handleConnectClick} className="btn btn-primary btn-full">
                      Connect
                    </button>
                  </div>
                </div>
              )}

              {/* VOICE CHAT — only shown when connected to at least one peer */}
              {connected && <div className="sidebar-section">
                <h3 className="section-label">Voice Chat</h3>
                {voiceError && (
                  <p className="error-text" style={{ fontSize: 12 }}>{voiceError}</p>
                )}
                {voiceActive ? (
                  <div className="voice-controls">
                    <button
                      onClick={toggleMute}
                      className={`btn-voice-control${(muted || hostMuted) ? ' btn-voice-muted' : ''}${hostMuted ? ' btn-voice-host-muted' : ''}`}
                      title={hostMuted ? 'Muted by host' : (muted ? 'Unmute mic' : 'Mute mic')}
                      disabled={hostMuted}
                    >
                      <span className="voice-btn-icon">{hostMuted ? '🔒' : (muted ? '🔇' : '🎙')}</span>
                      <span>{hostMuted ? 'Host muted' : (muted ? 'Unmute' : 'Mute')}</span>
                    </button>
                    <button
                      onClick={toggleNoiseSuppression}
                      className={`btn-voice-control${!noiseSuppression ? ' btn-voice-muted' : ''}`}
                      title={noiseSuppression ? 'Noise suppression on — click to disable' : 'Noise suppression off — click to enable'}
                    >
                      <span className="voice-btn-icon">{noiseSuppression ? '✦' : '〰'}</span>
                      <span>NS {noiseSuppression ? 'On' : 'Off'}</span>
                    </button>
                    <button
                      onClick={leaveVoice}
                      className="btn-voice-leave"
                      title="Leave voice chat"
                    >
                      ✕ Leave
                    </button>
                  </div>
                ) : (
                  <button onClick={joinVoice} className="btn btn-voice-join btn-full">
                    🎙 Join Voice
                  </button>
                )}
              </div>}

            </div>

            <Separator.Root className="sidebar-divider" />

            {/* PARTICIPANTS */}
            <div className="participants-area">
              <div className="participants-header">
                <h3 className="section-label" style={{ margin: 0 }}>Participants</h3>
                <span className="participants-badge">{participants.length}</span>
                {voiceParticipants.size > 0 && (
                  <span className="voice-count-badge" title="In voice call">
                    🎙 {voiceParticipants.size}
                  </span>
                )}
              </div>

              <ScrollArea.Root className="participants-scroll-root">
                <ScrollArea.Viewport className="participants-scroll-viewport">
                  {participants.map(peer => {
                    const inVoice = voiceParticipants.has(peer.id);
                    const isSpeaking = speakingPeers.has(peer.id);
                    const isLocal = peer.id === localId;

                    return (
                      <div key={peer.id} className="participant-item">
                        <Avatar.Root className="avatar-root">
                          <Avatar.Fallback
                            className="avatar-fallback"
                            style={{ backgroundColor: getAvatarColor(peer.id) }}
                          >
                            {(peer.name.charAt(0) || 'P').toUpperCase()}
                          </Avatar.Fallback>
                        </Avatar.Root>

                        <span className="participant-name">{peer.name}</span>

                        {/* Voice indicator icon */}
                        {inVoice && (
                          <span
                            className={`voice-indicator${isSpeaking ? ' speaking' : ''}`}
                            title={isSpeaking ? 'Speaking' : 'In voice'}
                          >
                            {isSpeaking ? '🔊' : '🎙'}
                          </span>
                        )}

                        {/* Mute peer button — only the host sees this, only for others in voice */}
                        {!isLocal && inVoice && localId === hostId && (
                          <button
                            onClick={() => requestMutePeer(peer.id)}
                            className={`btn-mute-peer${hostMutedPeers.has(peer.id) ? ' btn-mute-peer-active' : ''}`}
                            title={hostMutedPeers.has(peer.id) ? 'Unmute this participant' : 'Mute this participant'}
                          >
                            {hostMutedPeers.has(peer.id) ? '🔇' : '⊘'}
                          </button>
                        )}

                        {/* Disconnect — host only */}
                        {!isLocal && localId === hostId && (
                          <button
                            onClick={() => disconnectFromPeer(peer.id)}
                            className="btn-disconnect"
                            title="Disconnect"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    );
                  })}
                </ScrollArea.Viewport>
                <ScrollArea.Scrollbar className="radix-scrollbar" orientation="vertical">
                  <ScrollArea.Thumb className="radix-scrollbar-thumb" />
                </ScrollArea.Scrollbar>
              </ScrollArea.Root>
            </div>

            {/* YOUR IDENTITY — pinned to bottom */}
            <Separator.Root className="sidebar-divider" />
            <div className="sidebar-footer">
              <h3 className="section-label">Your Chat ID</h3>
              <div className="id-row">
                <span className="id-value">{localId}</span>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <button
                      onClick={handleCopy}
                      className={`copy-btn-icon${copied ? ' copied' : ''}`}
                      aria-label="Copy your ID"
                    >
                      {copied ? '✓' : '⎘'}
                    </button>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content className="tooltip-content" sideOffset={6}>
                      {copied ? 'Copied!' : 'Copy your ID'}
                      <Tooltip.Arrow className="tooltip-arrow" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </div>
              <h3 className="section-label" style={{ marginTop: 10 }}>Your Display Name</h3>
              <input
                type="text"
                value={username}
                onChange={handleUsernameChange}
                placeholder="Display name"
                className="input"
                style={{ fontSize: 13 }}
              />
            </div>
          </div>
        </aside>

        {/* ── Main Chat ── */}
        <main className="chat-main">
          {/* Topbar — mobile only */}
          <div className="chat-topbar">
            <button
              onClick={() => setSidebarOpen(o => !o)}
              className="btn-icon"
              aria-label="Toggle sidebar"
            >
              ☰
            </button>
            <span className="topbar-title">Chat</span>
            <div className={`status-dot ${connected ? 'online' : 'offline'}`} />
          </div>

          {/* Voice status bar — visible when in a voice call */}
          {voiceActive && (
            <div className={`voice-status-bar${(muted || hostMuted) ? ' voice-status-muted' : ''}`}>
              <span className="voice-status-pip" />
              <span className="voice-status-label">
                {hostMuted ? '🔒 Muted by host' : (muted ? '🔇 Muted' : '🎙 Voice active')}
                {voiceParticipants.size > 1 && (
                  <span className="voice-status-count"> · {voiceParticipants.size} in call</span>
                )}
              </span>
              <div className="voice-status-actions">
                <button
                  onClick={toggleMute}
                  disabled={hostMuted}
                  className={`btn-voice-sm${(muted || hostMuted) ? ' btn-voice-sm-muted' : ''}`}
                  title={hostMuted ? 'Muted by host' : undefined}
                >
                  {hostMuted ? 'Host muted' : (muted ? 'Unmute' : 'Mute')}
                </button>
                <button onClick={leaveVoice} className="btn-voice-sm btn-voice-sm-leave">
                  Leave
                </button>
              </div>
            </div>
          )}

          {/* Messages */}
          <ScrollArea.Root className="messages-scroll-root">
            <ScrollArea.Viewport className="messages-scroll-viewport">
              {messages.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-state-icon">💬</span>
                  <span>No messages yet.</span>
                  <span style={{ color: '#1e2a3a', fontSize: 13 }}>
                    Share your ID and connect with a peer to start chatting.
                  </span>
                </div>
              ) : (
                <div className="messages-list">
                  {messages.map(msg => {
                    const isOwn = msg.sender === localId;
                    const isSystem = msg.sender === 'system';

                    return (
                      <div
                        key={msg.id}
                        className={`message-row${isOwn ? ' row-own' : ''}${isSystem ? ' row-system' : ''}`}
                      >
                        {!isOwn && !isSystem && (
                          <Avatar.Root className="avatar-root-sm">
                            <Avatar.Fallback
                              className="avatar-fallback"
                              style={{
                                backgroundColor: getAvatarColor(msg.sender),
                                fontSize: 11
                              }}
                            >
                              {getParticipantName(msg.sender).charAt(0).toUpperCase()}
                            </Avatar.Fallback>
                          </Avatar.Root>
                        )}

                        {isSystem ? (
                          <p className="system-message-text">{msg.content}</p>
                        ) : (
                          <div className="message-bubble">
                            {!isOwn && (
                              <span className="message-sender-name">
                                {getParticipantName(msg.sender)}
                              </span>
                            )}
                            <p className="message-text">{msg.content}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea.Viewport>
            <ScrollArea.Scrollbar className="radix-scrollbar" orientation="vertical">
              <ScrollArea.Thumb className="radix-scrollbar-thumb" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>

          {/* Input Area */}
          <div className="chat-input-area">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={connected ? 'Type a message… (Enter to send)' : 'Connect to a peer to start chatting'}
              disabled={!connected}
              className="chat-textarea"
              rows={2}
            />
            <button
              onClick={handleSendMessage}
              disabled={!connected || !newMessage.trim()}
              className="btn btn-primary btn-send"
            >
              Send
            </button>
          </div>
        </main>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>
    </Tooltip.Provider>
  );
};

export default P2PChat;
